import React, { useEffect } from 'react';
import { Box, Paper, Typography, Stack, Grid, Chip, Table, TableHead, TableRow, TableCell, TableBody, Alert, AlertTitle, LinearProgress } from '@mui/material';
import { OptionsHeaderBar } from '../../components/options/OptionsHeaderBar';
import { useOptionsStore } from '../../store/useOptionsStore';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

const OptionsHealthPage: React.FC = () => {
  const { healthReport, fetchHealthReport } = useOptionsStore();

  useEffect(() => {
    fetchHealthReport();
    const interval = setInterval(fetchHealthReport, 3000);
    return () => clearInterval(interval);
  }, []);

  const components = healthReport?.components || [];
  const isHealthy = healthReport?.overallStatus === 'HEALTHY';
  const isEntriesAllowed = healthReport?.isEntriesAllowed ?? true;

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'HEALTHY':
        return <CheckCircleIcon sx={{ fontSize: 18, color: '#16A34A' }} />;
      case 'WARNING':
        return <WarningAmberIcon sx={{ fontSize: 18, color: '#D97706' }} />;
      case 'CRITICAL':
      case 'LOCKED':
      default:
        return <ErrorOutlineIcon sx={{ fontSize: 18, color: '#DC2626' }} />;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'HEALTHY':
        return <Chip size="small" label="🟢 HEALTHY" sx={{ bgcolor: 'rgba(34, 197, 94, 0.12)', color: '#16A34A', fontWeight: 700, fontSize: '0.7rem' }} />;
      case 'WARNING':
        return <Chip size="small" label="🟡 WARNING" sx={{ bgcolor: 'rgba(245, 158, 11, 0.12)', color: '#D97706', fontWeight: 700, fontSize: '0.7rem' }} />;
      case 'CRITICAL':
      case 'LOCKED':
      default:
        return <Chip size="small" label="🔴 CRITICAL" sx={{ bgcolor: 'rgba(239, 68, 68, 0.12)', color: '#DC2626', fontWeight: 700, fontSize: '0.7rem' }} />;
    }
  };

  return (
    <Box sx={{ p: 3, bgcolor: '#F8FAFC', minHeight: '100vh' }}>
      <OptionsHeaderBar />

      {/* Safety Alert Banner */}
      {!isEntriesAllowed && (
        <Alert severity="error" sx={{ mb: 2.5, borderRadius: 2 }}>
          <AlertTitle sx={{ fontWeight: 700 }}>AUTOMATED ENTRY LOCKOUT ACTIVE</AlertTitle>
          {healthReport?.lockoutReason || 'Critical component failure detected. New strategy entries are locked out for account safety.'}
        </Alert>
      )}

      {/* Overall Health Card */}
      <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: '1px solid #E2E8F0', bgcolor: '#FFFFFF', mb: 2.5 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems="center" spacing={2}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <HealthAndSafetyIcon sx={{ color: isHealthy ? '#16A34A' : '#DC2626', fontSize: 32 }} />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#1E293B' }}>
                NIFTY OPTIONS CONTINUOUS SYSTEM HEALTH MONITOR
              </Typography>
              <Typography variant="caption" sx={{ color: '#64748B' }}>
                12-vector real-time diagnostics • Probing latency, feed integrity, certificate validity & risk bounds
              </Typography>
            </Box>
          </Stack>

          <Stack direction="row" spacing={1.5} alignItems="center">
            <Chip
              label={isHealthy ? '🟢 SYSTEM HEALTHY' : isEntriesAllowed ? '🟡 DEGRADED (ENTRIES ACTIVE)' : '🔴 CRITICAL (ENTRIES LOCKED)'}
              sx={{
                fontWeight: 800,
                fontSize: '0.75rem',
                bgcolor: isHealthy ? 'rgba(34, 197, 94, 0.12)' : isEntriesAllowed ? 'rgba(245, 158, 11, 0.12)' : 'rgba(239, 68, 68, 0.12)',
                color: isHealthy ? '#16A34A' : isEntriesAllowed ? '#D97706' : '#DC2626',
              }}
            />
          </Stack>
        </Stack>
      </Paper>

      {/* 12-Component Diagnostic Matrix */}
      <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: '1px solid #E2E8F0', bgcolor: '#FFFFFF' }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1E293B', mb: 2 }}>
          12-VECTOR SUBSYSTEM DIAGNOSTIC MATRIX
        </Typography>

        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#0F172A', '& th': { color: '#FFFFFF', fontWeight: 600, fontSize: '0.72rem' } }}>
              <TableCell>SUBSYSTEM / COMPONENT</TableCell>
              <TableCell>CATEGORY</TableCell>
              <TableCell align="center">STATUS</TableCell>
              <TableCell align="right">LATENCY</TableCell>
              <TableCell>DIAGNOSTIC TELEMETRY</TableCell>
              <TableCell align="center">CRITICAL GATE</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {components.map((c) => (
              <TableRow key={c.id} hover sx={{ '& td': { fontSize: '0.78rem', py: 1.1 } }}>
                <TableCell sx={{ fontWeight: 600, color: '#1E293B' }}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    {getStatusIcon(c.status)}
                    <span>{c.name}</span>
                  </Stack>
                </TableCell>
                <TableCell>
                  <Chip size="small" label={c.category} sx={{ height: 20, fontSize: '0.65rem', bgcolor: '#F1F5F9' }} />
                </TableCell>
                <TableCell align="center">{getStatusBadge(c.status)}</TableCell>
                <TableCell align="right" sx={{ fontFamily: "'Roboto Mono', monospace", fontWeight: 600, color: c.latencyMs && c.latencyMs < 50 ? '#16A34A' : '#D97706' }}>
                  {c.latencyMs !== undefined ? `${c.latencyMs} ms` : '—'}
                </TableCell>
                <TableCell sx={{ color: '#475569' }}>{c.details}</TableCell>
                <TableCell align="center">
                  <Chip
                    size="small"
                    label={c.isCritical ? 'CRITICAL' : 'OPTIONAL'}
                    sx={{
                      height: 18,
                      fontSize: '0.62rem',
                      fontWeight: 700,
                      bgcolor: c.isCritical ? 'rgba(239, 68, 68, 0.10)' : '#F1F5F9',
                      color: c.isCritical ? '#DC2626' : '#64748B',
                    }}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
};

export default OptionsHealthPage;
